<template>
  <div>
    <loading-panel v-if="(!profile || loading) && !error"/>
    <card card-body-classes="table-full-width" v-else>
      <div slot="header" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
        <h4 class="card-title">{{ $t('reportingProfiles.reportingProfileAccountsTable.title') }}({{
            profile.accounts.length
          }})</h4>
          <add-user-to-reporting-profile v-if="$auth.can({reporting_profile: 'update'}, 'permissions')"/>
      </div>
      <div>
        <div class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
          <el-select
            class="select-primary mb-3 pagination-select"
            v-model="pagination.perPage"
            placeholder="Per page"
          >
            <el-option
              class="select-primary"
              v-for="item in pagination.perPageOptions"
              :key="item"
              :label="item"
              :value="item"
            >
            </el-option>
          </el-select>


          <div class="row">
            <div class="col-6">
              <base-input>
                <el-input
                  type="search"
                  class="mb-3 search-input"
                  prefix-icon="el-icon-search"
                  :placeholder="$t('generic.searchInTablePlaceholder')"
                  v-model="searchQuery"
                  aria-controls="datatables"
                >
                </el-input>
              </base-input>
            </div>

          </div>
        </div>

        <el-table v-loading="loading" :data="queriedData" :empty-text="$t('generic.noData')">
          <el-table-column
            v-for="column in tableColumns"
            :key="column.label"
            :min-width="column.minWidth"
            :prop="column.prop"
            :label="column.label"
            :formatter="column.formatter"
          >
          </el-table-column>
          <el-table-column align="right" width="100">
            <div slot-scope="props">
              <el-tooltip :content="$t('reportingProfiles.reportingProfileAccountsTable.removeHint')"
                          effect="light" :open-delay="300" placement="top">
                <base-button class="remove btn-link mr-1" type="danger" size="sm" icon
                             @click.native="handleDelete(props.row)">
                  <i class="tim-icons icon-simple-remove"></i>
                </base-button>
              </el-tooltip>
              <router-link
                :to="{name: 'account-journeys', params: {id: props.row.account_id}}">
                <i class="tim-icons icon-double-right"></i>
              </router-link>
            </div>
          </el-table-column>
        </el-table>
      </div>
      <div
        slot="footer"
        class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
      >
        <div class="">
          <p class="card-category">
            {{ $t('generic.tablePaginationSummary', {from: from + 1, to: to, total: total}) }}
          </p>
        </div>
        <base-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        >
        </base-pagination>
      </div>
    </card>
  </div>
</template>

<script>
import {Option, Select, Table, TableColumn} from 'element-ui';
import {BaseAlert, BasePagination} from 'src/components';
import Fuse from 'fuse.js';
import {mapActions, mapState} from "vuex";
import LoadingPanel from "@/components/LoadingPanel.vue";
import swal from "sweetalert2";
import AddUserToReportingProfile from "@/pages/ReportingProfiles/ReportingProfileDetails/AddUserToReportingProfile.vue";

export default {
  name: 'reporting-profile-accounts',
  components: {
    AddUserToReportingProfile,
    LoadingPanel,
    BasePagination,
    BaseAlert,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  methods: {
    ...mapActions(['DeleteAccountFromReportingProfile', 'GetReportingProfile']),
    handleDelete(row) {
      swal({
        title: this.$t('reportingProfiles.reportingProfileAccountsTable.deleteConfirm'),
        text: row.account_id,
        confirmButtonText: this.$t('reportingProfiles.reportingProfileAccountsTable.deleteActionButton'),
        cancelButtonText: this.$t('reportingProfiles.reportingProfileAccountsTable.deleteCancelButton'),
        buttonsStyling: false,
        confirmButtonClass: 'btn btn-danger',
        cancelButtonClass: 'btn btn-secondary',
        type: 'question',
        showCancelButton: true
      }).then(result => {
        if (result.value) {
          // this.deleteRow(row);
          const payload = {
            profile_id: this.$route.params.id,
            id: row.id
          }
          this.DeleteAccountFromReportingProfile(payload).then(() => {
              if (this.error === null) {
                this.$notify({
                  type: 'success',
                  message: this.$t('reportingProfiles.reportingProfileAccountsTable.accountDeletedNotification', {account_id: row.account_id}),
                  icon: 'tim-icons icon-alert-circle-exc'
                })
                this.GetReportingProfile(this.$route.params.id)
              }
            }
          )
        }
      });
    },
    deleteRow(row) {
      let indexToDelete = this.tableData.findIndex(
        tableRow => tableRow.id === row.id
      );
      if (indexToDelete >= 0) {
        this.tableData.splice(indexToDelete, 1);
      }
    },
  },
  computed: {
    ...mapState({
      error: state => state.reporting_profile.error,
      loading: state => state.reporting_profile.loading,
      profile: state => state.reporting_profile.profile,
    }),
    /***
     * Returns a page from the searched data or the whole data. Search is performed in the watch section below
     */
    queriedData() {
      let result = this.tableData;
      if (this.searchedData.length > 0) {
        result = this.searchedData;
      }
      return result.slice(this.from, this.to);
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchedData.length > 0
        ? this.searchedData.length
        : this.tableData.length;
    },
    // Table columns need to be computed to dynamically change the headers when language is selected
    tableColumns() {
      return [
        {
          prop: 'created_at',
          label: this.$t('generic.created'),
          formatter: (row, column, cellValue, index) => {
            return this.$options.filters.time(cellValue)
          },
          minWidth: 120
        },
        {
          prop: 'identifier',
          label: this.$t('reportingProfiles.reportingProfileAccountsTable.identifier'),
          minWidth: 150
        },
        {
          prop: 'account_type',
          label: this.$t('reportingProfiles.reportingProfileAccountsTable.accountType'),
          minWidth: 120
        },
        {
          prop: 'account_id',
          label: this.$t('reportingProfiles.reportingProfileAccountsTable.accountId'),
          minWidth: 150
        },
      ]
    }
  },
  data() {
    return {
      pagination: {
        perPage: 50,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['identifier'],
      tableData: [],
      searchedData: [],
      fuseSearch: null
    };
  },

  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData;
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery);
      }
      this.searchedData = result;
    },
    profile(profile) {
      this.tableData = profile.accounts ? profile.accounts : [];
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: this.propsToSearch,
        threshold: 0.1
      });
    }
  }
};
</script>
